import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import AppConfig from '../../appConfig';

const OWNER_FIELD_NAMES = [
    'owner_name',
    'owner_last_name',
    'owner_phone',
    'owner_email',
    'owner_website',
    'owner_company_name',
    'owner_title',
    'management_company_name',
];

const LONG_TEXT_FIELDS = [
    'project_description',
    'project_notes',
    'zoning_description',
    'mailing_addresses',
];

const isOwnerField = key =>
    key.indexOf('owner_') === 0 || OWNER_FIELD_NAMES.indexOf(key) !== -1;

const isIsoDateString = value =>
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}/.test(value) &&
    !isNaN(Date.parse(value));

const toInputValue = value => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return String(value);
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const toDisplayValue = value => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const detectInputType = (key, value) => {
    if (typeof value === 'boolean') return 'checkbox';
    if (typeof value === 'number') return 'number';
    if (isIsoDateString(value) && /date|_at$/i.test(key)) return 'date';
    if (LONG_TEXT_FIELDS.indexOf(key) !== -1) return 'textarea';
    return 'text';
};

class QAFeedbackModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            loadError: null,
            originalFields: {},
            fields: {},
            editing: {},
            orderedKeys: [],
            isSubmitting: false,
            submitError: null,
            submitResult: null,
        };
        this.portalNode = document.createElement('div');
    }

    componentDidMount() {
        document.body.appendChild(this.portalNode);
        this.renderPortal();
        this.fetchFacility();
    }

    componentDidUpdate() {
        this.renderPortal();
    }

    componentWillUnmount() {
        ReactDOM.unmountComponentAtNode(this.portalNode);
        if (this.portalNode.parentNode) {
            this.portalNode.parentNode.removeChild(this.portalNode);
        }
    }

    renderPortal = () => {
        ReactDOM.unstable_renderSubtreeIntoContainer(
            this,
            this.renderContent(),
            this.portalNode,
        );
    };

    fetchFacility = () => {
        const { facilityId } = this.props;
        if (!facilityId) return;

        this.setState({
            isLoading: true,
            loadError: null,
            submitError: null,
            submitResult: null,
        });

        const accountsUrl = AppConfig.constants.backends.accounts;
        const url = `${accountsUrl}/api/v1/data/selfstorage-facility/${encodeURIComponent(
            facilityId,
        )}`;

        fetch(url, { method: 'GET', credentials: 'include' })
            .then(res => {
                if (!res.ok) {
                    throw new Error(`Failed to load facility (${res.status})`);
                }
                return res.json();
            })
            .then(data => {
                const payload = data || {};
                this.setState({
                    fields: Object.assign({}, payload),
                    originalFields: Object.assign({}, payload),
                    editing: {},
                    orderedKeys: Object.keys(payload),
                    isLoading: false,
                });
            })
            .catch(err => {
                this.setState({
                    loadError: err.message || 'Failed to load facility',
                    isLoading: false,
                });
            });
    };

    handleFieldChange = (key, value) => {
        this.setState(prevState => ({
            fields: Object.assign({}, prevState.fields, { [key]: value }),
        }));
    };

    startEditing = key => {
        this.setState(prevState => ({
            editing: Object.assign({}, prevState.editing, { [key]: true }),
        }));
    };

    revertEditing = key => {
        this.setState(prevState => {
            const nextEditing = Object.assign({}, prevState.editing);
            delete nextEditing[key];
            return {
                editing: nextEditing,
                fields: Object.assign({}, prevState.fields, {
                    [key]: prevState.originalFields[key],
                }),
            };
        });
    };

    computeChangedFields = () => {
        const { editing, fields, originalFields } = this.state;
        const changed = {};
        Object.keys(editing).forEach(key => {
            if (!editing[key]) return;
            const current = fields[key];
            const original = originalFields[key];
            const inputType = detectInputType(key, original);
            let normalized = current;
            if (inputType === 'number') {
                normalized = current === '' || current === null || current === undefined
                    ? null
                    : Number(current);
            }
            if (normalized !== original) {
                changed[key] = normalized;
            }
        });
        return changed;
    };

    handleSubmit = event => {
        if (event) event.preventDefault();
        const { facilityId } = this.props;
        const changed = this.computeChangedFields();

        if (Object.keys(changed).length === 0) return;

        this.setState({
            isSubmitting: true,
            submitError: null,
            submitResult: null,
        });

        const accountsUrl = AppConfig.constants.backends.accounts;
        const url = `${accountsUrl}/api/v1/qa-feedback/facility`;

        fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ facility_id: facilityId, fields: changed }),
        })
            .then(res =>
                res
                    .json()
                    .catch(() => ({}))
                    .then(body => {
                        if (!res.ok) {
                            throw new Error(
                                (body && body.error) || `Request failed (${res.status})`,
                            );
                        }
                        return body;
                    }),
            )
            .then(body => {
                this.setState({
                    submitResult: body,
                    isSubmitting: false,
                });
            })
            .catch(err => {
                this.setState({
                    submitError: err.message || 'Submission failed',
                    isSubmitting: false,
                });
            });
    };

    renderEditor = (key, inputType, controlId) => {
        const value = this.state.fields[key];

        if (inputType === 'checkbox') {
            return (
                <input
                    id={controlId}
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={e => this.handleFieldChange(key, e.target.checked)}
                />
            );
        }

        if (inputType === 'textarea') {
            return (
                <textarea
                    id={controlId}
                    rows={3}
                    value={toInputValue(value)}
                    onChange={e => this.handleFieldChange(key, e.target.value)}
                    className="qa-feedback__input"
                />
            );
        }

        const htmlType =
            inputType === 'number'
                ? 'number'
                : inputType === 'date'
                    ? 'date'
                    : 'text';
        const rawValue = toInputValue(value);
        const displayValue =
            inputType === 'date' && rawValue ? rawValue.slice(0, 10) : rawValue;

        return (
            <input
                id={controlId}
                type={htmlType}
                value={displayValue}
                onChange={e => this.handleFieldChange(key, e.target.value)}
                className="qa-feedback__input"
            />
        );
    };

    renderField = key => {
        const { fields, editing } = this.state;
        const value = fields[key];
        const inputType = detectInputType(key, value);
        const controlId = `qa-feedback-${key}`;
        const isEditing = !!editing[key];

        return (
            <div key={key} className="qa-feedback__row">
                <label htmlFor={controlId} className="qa-feedback__label">
                    {key}
                </label>
                <div className="qa-feedback__value-row">
                    {isEditing ? (
                        <div className="qa-feedback__editor">
                            {this.renderEditor(key, inputType, controlId)}
                        </div>
                    ) : (
                        <span className="qa-feedback__value">{toDisplayValue(value)}</span>
                    )}
                    <button
                        type="button"
                        className="qa-feedback__icon-btn"
                        onClick={() =>
                            isEditing ? this.revertEditing(key) : this.startEditing(key)
                        }
                        aria-label={isEditing ? `Revert ${key}` : `Edit ${key}`}
                        title={isEditing ? 'Revert' : 'Edit'}
                    >
                        <i className="material-icons md-18">
                            {isEditing ? 'undo' : 'edit'}
                        </i>
                    </button>
                </div>
            </div>
        );
    };

    render() {
        return null;
    }

    renderContent() {
        const { onClose } = this.props;
        const {
            isLoading,
            loadError,
            orderedKeys,
            isSubmitting,
            submitError,
            submitResult,
        } = this.state;

        const facilityKeys = [];
        const ownerKeys = [];
        orderedKeys.forEach(key => {
            if (isOwnerField(key)) ownerKeys.push(key);
            else facilityKeys.push(key);
        });

        const changed = this.computeChangedFields();
        const hasChanges = Object.keys(changed).length > 0;

        return (
            <div
                className="qa-feedback__overlay"
                role="dialog"
                aria-modal="true"
                onClick={e => {
                    if (e.target === e.currentTarget && !isSubmitting) onClose();
                }}
            >
                <div className="qa-feedback__dialog">
                    <form onSubmit={this.handleSubmit} className="qa-feedback__form">
                        <div className="qa-feedback__header">
                            <h3 className="qa-feedback__title">
                                Report incorrect facility information
                            </h3>
                            <button
                                type="button"
                                className="qa-feedback__close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                <i className="material-icons">close</i>
                            </button>
                        </div>
                        <div className="qa-feedback__scroll">
                            <div className="qa-feedback__body">
                                {isLoading && (
                                    <div className="qa-feedback__loading">
                                        <i className="material-icons md-18 opacity-054">
                                            hourglass_empty
                                        </i>
                                        <span>Loading facility...</span>
                                    </div>
                                )}
                                {loadError && !isLoading && (
                                    <div className="qa-feedback__error">{loadError}</div>
                                )}
                                {!isLoading && !loadError && orderedKeys.length > 0 && (
                                    <div>
                                        <h4 className="qa-feedback__section-title">
                                            Facility information
                                        </h4>
                                        <div className="qa-feedback__grid">
                                            {facilityKeys.map(this.renderField)}
                                        </div>
                                        {ownerKeys.length > 0 && (
                                            <div>
                                                <h4 className="qa-feedback__section-title qa-feedback__section-title--owner">
                                                    Owner information
                                                </h4>
                                                <div className="qa-feedback__grid">
                                                    {ownerKeys.map(this.renderField)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {submitError && (
                                    <div className="qa-feedback__error qa-feedback__error--inline">
                                        {submitError}
                                    </div>
                                )}
                                {submitResult && (
                                    <div className="qa-feedback__success">
                                        Feedback submitted.{' '}
                                        {submitResult.url ? (
                                            <a
                                                href={submitResult.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Open sheet
                                            </a>
                                        ) : (
                                            'Sheet created.'
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="qa-feedback__footer">
                            {!hasChanges && !isLoading && !loadError && !submitResult && (
                                <span className="qa-feedback__empty-hint">
                                    Nothing to submit
                                </span>
                            )}
                            <button
                                type="button"
                                className="btn-flat"
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                {submitResult ? 'Close' : 'Cancel'}
                            </button>
                            <button
                                type="submit"
                                className="btn-raised"
                                disabled={
                                    isLoading ||
                                    isSubmitting ||
                                    !!loadError ||
                                    !hasChanges
                                }
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit feedback'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}

QAFeedbackModal.propTypes = {
    facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onClose: PropTypes.func.isRequired,
};

export default QAFeedbackModal;